import React from "react";

interface RealityCheckMetrics {
  budgetGap: number;
  timelineGap: number;
  scopeRisk: number;
  changeImpact: number;
  alignmentScore: number;
  assessment:
    | "Proceed with Confidence"
    | "Addressable Challenges"
    | "Significant Obstacles"
    | "Major Rethinking Needed";
  recommendation: string;
}

interface RealityCheckPentagonProps {
  metrics: RealityCheckMetrics;
}

// Color functions for Reality Check (problem-focused)
const getGapColor = (gap: number): string => {
  if (gap > 20) return "#10b981"; // Green - good buffer
  if (gap > 0) return "#f59e0b"; // Amber - just enough
  if (gap > -20) return "#f97316"; // Orange - concerning
  return "#ef4444"; // Red - major problem
};

const getRiskColor = (risk: number): string => {
  if (risk < 20) return "#10b981"; // Green - low risk
  if (risk < 40) return "#f59e0b"; // Amber - moderate
  if (risk < 60) return "#f97316"; // Orange - high
  return "#ef4444"; // Red - very high
};

const getAssessmentColor = (
  assessment: RealityCheckMetrics["assessment"],
): string => {
  switch (assessment) {
    case "Proceed with Confidence":
      return "#10b981";
    case "Addressable Challenges":
      return "#f59e0b";
    case "Significant Obstacles":
      return "#f97316";
    case "Major Rethinking Needed":
      return "#ef4444";
  }
};

export const RealityCheckPentagon: React.FC<RealityCheckPentagonProps> = ({
  metrics,
}) => {
  const centerX = 225;
  const centerY = 225;
  const maxRadius = 120;

  // Pentagon vertices (5 points, starting from top, going clockwise)
  const getVertex = (index: number, value: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    const radius = (value / 100) * maxRadius;
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  // Reference pentagon (max values)
  const refVertices = [0, 1, 2, 3, 4].map((i) => getVertex(i, 100));
  const refPath =
    refVertices
      .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
      .join(" ") + " Z";

  // Data pentagon - normalize gaps to 0-100 scale
  const dataValues = [
    Math.max(0, Math.min(100, (metrics.budgetGap + 50) * 1)), // -50 to +50 → 0 to 100
    Math.max(0, Math.min(100, (metrics.timelineGap + 50) * 1)), // -50 to +50 → 0 to 100
    100 - metrics.scopeRisk, // Invert so low risk = good
    100 - metrics.changeImpact, // Invert so low impact = good
    metrics.alignmentScore,
  ];

  const dataVertices = dataValues.map((value, index) =>
    getVertex(index, value),
  );
  const dataPath =
    dataVertices
      .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
      .join(" ") + " Z";

  // Labels and icons for each vertex
  const labels = [
    {
      text: "Budget Gap",
      icon: "💰",
      color: getGapColor(metrics.budgetGap),
      value: metrics.budgetGap,
      display: `${metrics.budgetGap > 0 ? "+" : ""}${metrics.budgetGap}`,
    },
    {
      text: "Timeline Gap",
      icon: "⏰",
      color: getGapColor(metrics.timelineGap),
      value: metrics.timelineGap,
      display: `${metrics.timelineGap > 0 ? "+" : ""}${metrics.timelineGap}`,
    },
    {
      text: "Scope Risk",
      icon: "📋",
      color: getRiskColor(metrics.scopeRisk),
      value: metrics.scopeRisk,
      display: `${Math.round(metrics.scopeRisk)}%`,
    },
    {
      text: "Change Impact",
      icon: "🔄",
      color: getRiskColor(metrics.changeImpact),
      value: metrics.changeImpact,
      display: `${Math.round(metrics.changeImpact)}%`,
    },
    {
      text: "Alignment",
      icon: "🎯",
      color: getGapColor(metrics.alignmentScore - 50),
      value: metrics.alignmentScore,
      display: `${Math.round(metrics.alignmentScore)}%`,
    },
  ];

  // Get label position
  const getLabelPosition = (index: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    const labelRadius = maxRadius + 50;
    return {
      x: centerX + labelRadius * Math.cos(angle),
      y: centerY + labelRadius * Math.sin(angle),
    };
  };

  const assessmentColor = getAssessmentColor(metrics.assessment);

  return (
    <div className="bg-white rounded-2xl p-2 sm:p-8 shadow-xl border-2 border-slate-200">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-1">
          Reality Check Model
        </h2>
        <p className="text-sm text-slate-600 mb-2">
          <strong>Undecided if you should move forward?</strong> Use this view.
        </p>
        <p className="text-xs text-slate-500 leading-relaxed">
          This model is for companies questioning their commitment—uncertain
          about budget allocation, timeline investment, or motivation to build
          long-term scaling systems. Our assessment, problem identification, and
          solution architecture services help you understand what benefits you
          could gain from new systems, software, and AI solutions before making
          the decision.
        </p>
      </div>

      <div className="flex justify-center">
        <svg
          width="450"
          height="450"
          viewBox="0 0 450 450"
          className="w-full h-auto"
        >
          <defs>
            <clipPath id="realityLabelClip">
              <rect x="-54" y="-20" width="108" height="40" rx="6" />
            </clipPath>
          </defs>

          {/* Grid circles */}
          {[20, 40, 60, 80, 100].map((percent) => {
            const radius = (percent / 100) * maxRadius;
            return (
              <circle
                key={percent}
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray={percent === 100 ? "none" : "3,3"}
              />
            );
          })}

          {/* Grid lines from center to vertices */}
          {[0, 1, 2, 3, 4].map((i) => {
            const vertex = getVertex(i, 100);
            return (
              <line
                key={i}
                x1={centerX}
                y1={centerY}
                x2={vertex.x}
                y2={vertex.y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}

          {/* Reference pentagon outline */}
          <path
            d={refPath}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="2"
            strokeDasharray="5,5"
          />

          {/* Data pentagon (filled) */}
          <path
            d={dataPath}
            fill={assessmentColor}
            fillOpacity="0.2"
            stroke={assessmentColor}
            strokeWidth="3"
            className="transition-all duration-500"
          />

          {/* Vertex points */}
          {dataVertices.map((vertex, index) => (
            <circle
              key={index}
              cx={vertex.x}
              cy={vertex.y}
              r="6"
              fill={labels[index].color}
              stroke="white"
              strokeWidth="2"
              className="transition-all duration-500"
            />
          ))}

          {/* Labels with icons and values */}
          {labels.map((label, index) => {
            const pos = getLabelPosition(index);

            return (
              <g key={index}>
                <rect
                  x={pos.x - 54}
                  y={pos.y - 20}
                  width="108"
                  height="40"
                  rx="6"
                  fill="white"
                  stroke={label.color}
                  strokeWidth="2"
                  opacity="0.95"
                />
                <g
                  clipPath="url(#realityLabelClip)"
                  transform={`translate(${pos.x}, ${pos.y})`}
                >
                  <text
                    x={0}
                    y={-3}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="600"
                    fill="#334155"
                  >
                    {label.icon} {label.text}
                  </text>
                  <text
                    x={0}
                    y={12}
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="900"
                    fill={label.color}
                  >
                    {label.display}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Center label - Assessment */}
          <circle
            cx={centerX}
            cy={centerY}
            r="45"
            fill="white"
            stroke={assessmentColor}
            strokeWidth="2.5"
          />
          <text
            x={centerX}
            y={centerY - 8}
            textAnchor="middle"
            className="text-[8px] font-semibold"
            fill="#64748b"
          >
            Assessment
          </text>
          <text
            x={centerX}
            y={centerY + 6}
            textAnchor="middle"
            className="text-[9px] font-black leading-tight"
            fill={assessmentColor}
          >
            <tspan x={centerX} dy="0">
              {metrics.assessment.split(" ")[0]}
            </tspan>
            <tspan x={centerX} dy="11">
              {metrics.assessment.split(" ").slice(1, 3).join(" ")}
            </tspan>
            {metrics.assessment.split(" ").length > 3 && (
              <tspan x={centerX} dy="11">
                {metrics.assessment.split(" ")[3]}
              </tspan>
            )}
          </text>
        </svg>
      </div>
    </div>
  );
};
